/**
 * The dashboard's **Intel extras** (spec 2026-10-09 §2.2 tab 6, W4 card C5): registers Intel › Markets, Stations and
 * Charts (`registerDashSection`, orders from `tabs/intel/intel.json`, after M1's Wire and Charter). Read-only over the
 * Career (`m2`: the price log and the clock), the strategic client (this system's live view; the docked station) and
 * the Reach (charts, known stations, Set course).
 *
 * The only state it keeps is a session memory of the system views the client pushed (`onSystemView`), stamped with
 * the career game time: the Stations section shows a visited station's economy as last seen, and the Markets section
 * names a logged price's station. Nothing is saved (no new save field). `frame` only re-attaches to a new client: a
 * map lookup and a compare, no allocation (D-014).
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import type { Career } from '../../career/types'
import { loadExploreConfig } from '../../exploration/config'
import type { StrategicClient } from '../../strategic/client'
import { strategicMarketFor } from '../../strategic-link/market'
import { loadDashboardConfig } from '../../ui/dashboard/config'
import { createReachStationDirectory, setCourseTo } from '../../ui/dashboard/data'
import type { StationDirectory } from '../../ui/dashboard/data/types'
import { dashboardOpen, registerDashSection } from '../../ui/dashboard/registry'
import type { SeenView, ViewStations } from '../../ui/dashboard/tabs/intel/model'
import { createIntelSections } from '../../ui/dashboard/tabs/intel/sections'
import { systemNameIn } from '../../ui/strategic/names'
import { Pos64 } from '../../world/components'

/** After the strategic client and the Ledger (strategic + 6). */
const ORDER = FEATURE_ORDER.strategic + 7

export interface IntelTabFeature extends GameFeature {
  frame(ctx: GameContext): void
  dispose(): void
}

export function createIntelTabFeature(): IntelTabFeature {
  const offs: (() => void)[] = []
  const seen = new Map<string, SeenView>()
  let attached: StrategicClient | null = null
  let offView: (() => void) | null = null
  let gameTime: () => number = () => 0

  const attach = (c: StrategicClient | undefined): void => {
    if (c === undefined || c === attached) return
    offView?.()
    attached = c
    seen.clear()
    offView = c.onSystemView((v) => {
      seen.set(v.systemId, { systemId: v.systemId, stations: v.stations, at: gameTime() })
    })
  }

  return {
    name: 'intelTab',
    order: ORDER,
    init(ctx: GameContext): void {
      const reach = ctx.reach
      if (reach === null) return
      const client = (): StrategicClient | undefined => ctx.services.maybe('strategic')
      const career = (): Career | null => ctx.services.maybe('m2')?.career() ?? null
      gameTime = () => career()?.gameTime ?? 0
      attach(client())
      const hereSystemId = (): string | null => reach.session.layout?.systemId ?? null
      let dir: StationDirectory | null = null
      const directory = (): StationDirectory => {
        dir ??= createReachStationDirectory({
          galaxy: reach.galaxy,
          state: reach.state,
          layoutOf: (id) => reach.layoutOf(id),
          hereSystemId,
          herePosition: () => {
            const p = ctx.sim.playerEid
            return p < 0 ? null : [Pos64.x[p], Pos64.y[p], Pos64.z[p]]
          },
        })
        return dir
      }
      const sections = createIntelSections({
        dash: loadDashboardConfig(),
        career,
        here: (): ViewStations | null => {
          const c = client()
          attach(c)
          const v = c?.systemView ?? null
          const here = hereSystemId()
          return v !== null && v.systemId === here ? v : null
        },
        dockedStationId: () => {
          const c = client()
          const docked = ctx.services.maybe('m2')?.ui.getState().stationId ?? null
          return c === undefined || docked === null ? null : strategicMarketFor(c).resolve(docked)
        },
        seen: () => seen,
        charts: () => reach.state.charts,
        firstMult: loadExploreConfig().charts.firstDiscoveryMult,
        directory,
        setCourse: (d, stationId) => {
          setCourseTo(reach, d, stationId)
        },
        systemName: (id) => systemNameIn(reach.galaxy, id) ?? id,
        isOpen: dashboardOpen,
      })
      for (const s of sections) offs.push(registerDashSection(s))
    },
    frame(ctx: GameContext): void {
      if (offs.length === 0) return
      const c = ctx.services.maybe('strategic')
      if (c !== undefined && c !== attached) attach(c)
    },
    dispose(): void {
      for (const off of offs.splice(0)) off()
      offView?.()
      offView = null
      attached = null
      seen.clear()
    },
  }
}

const feature = createIntelTabFeature()

export default feature
